import { Calendar, ChevronDown } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useInfiniteEconomicCyclesQuery } from '@/hooks/business/useEconomicCyclesQuery'
import { cn } from '@/lib/utils'

// Helper function to format date range
const formatDateRange = (dateFrom: string, dateTo: string) => {
  const fromDate = new Date(dateFrom).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const toDate = new Date(dateTo).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  return `${fromDate} - ${toDate}`
}

interface EconomicCycleFilterProps {
  title: string
  icon?: React.ReactNode
  selectedValues: number[]
  onSelectionChange: (_values: number[]) => void
  placeholder?: string
  className?: string
}

export const EconomicCycleFilter = ({
  title,
  icon,
  selectedValues,
  onSelectionChange,
  placeholder = 'Seleccionar ciclo económico...',
  className,
}: EconomicCycleFilterProps) => {
  const [open, setOpen] = useState(false)
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  const { economicCycles = [], isLoading: isLoadingEconomicCycles } =
    useInfiniteEconomicCyclesQuery({
      businessId: businessData?.id ?? 0,
      params: {
        dateFrom: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
        dateTo: new Date().toISOString().split('T')[0],
      },
      options: {
        enabled: Boolean(businessData?.id && !isLoadingBusinessData),
      },
    })

  const handleToggle = useCallback(
    (cycleId: number) => {
      if (selectedValues.includes(cycleId)) {
        onSelectionChange(selectedValues.filter((id) => id !== cycleId))
      } else {
        onSelectionChange([...selectedValues, cycleId])
      }
    },
    [selectedValues, onSelectionChange]
  )

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-label={title}
          className={cn('gap-1.5', className)}
          variant="outline"
        >
          {icon ? icon : <Calendar className="h-4 w-4" />}
          <span>{title}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
          {selectedValues.length > 0 && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {selectedValues.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>
              {isLoadingEconomicCycles
                ? 'Cargando ciclos económicos...'
                : 'No se encontraron ciclos económicos.'}
            </CommandEmpty>
            <CommandGroup>
              {isLoadingEconomicCycles
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div className="flex items-center space-x-2 p-2" key={i}>
                      <Skeleton className="h-4 w-4" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  ))
                : economicCycles.map((cycle) => (
                    <CommandItem
                      className="flex cursor-pointer items-center justify-between p-2"
                      key={cycle.id}
                      onSelect={() => handleToggle(cycle.id)}
                    >
                      <div className="flex-1">
                        <div className="font-medium">
                          {cycle.name || `Ciclo ${cycle.id}`}{' '}
                          {cycle.isActive ? '(Activo)' : '(Cerrado)'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {cycle.closedDate
                            ? formatDateRange(cycle.openDate, cycle.closedDate)
                            : `Desde: ${new Date(
                                cycle.openDate
                              ).toLocaleDateString('es-ES')}`}
                        </div>
                      </div>
                      {selectedValues.includes(cycle.id) && (
                        <div className="ml-2 h-2 w-2 rounded-full bg-blue-600" />
                      )}
                    </CommandItem>
                  ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
